// assets/related-tiers-element.js

import { planTiers, narrowShelf, buildShelfUrl, buildCollectionUrl, claimUnique } from './related-tiers.js';

/**
 * Fetches every shelf in parallel once the block nears the viewport, verifies
 * and scopes what comes back, de-duplicates across shelves, then renders
 * whatever survives. Anything too thin is removed rather than padded.
 */
class RelatedTiers extends HTMLElement {
  connectedCallback() {
    if (this.observed) return;
    this.observed = true;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.load().catch(() => this.remove());
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }

  get plan() {
    const node = document.getElementById(this.dataset.plan);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  async load() {
    const plan = this.plan;
    const tiers = planTiers(plan);
    if (!tiers.length) return this.remove();

    const fetched = await Promise.all(
      tiers.map((tier) => this.fetchShelf(plan, tier).catch(() => null))
    );
    const shelves = fetched
      .filter(Boolean)
      .map((shelf) => narrowShelf(shelf, plan.minCards, [plan.currentId]));
    const kept = claimUnique(shelves, plan.minCards, plan.maxCards, [plan.currentId]);

    if (!kept.length) return this.remove();
    kept.forEach((shelf) => this.render(shelf, plan));
    this.classList.add('related-tiers--loaded');
  }

  async fetchShelf(plan, tier) {
    const response = await fetch(buildShelfUrl(plan, tier));
    if (!response.ok) return null;

    const holder = document.createElement('div');
    holder.innerHTML = await response.text();

    const facetNode = holder.querySelector('.related-tier__facets');
    if (!facetNode) return null;

    let facets;
    try {
      facets = JSON.parse(facetNode.textContent);
    } catch {
      // Unverifiable products must never be rendered unchecked.
      return null;
    }

    const cards = new Map();
    holder.querySelectorAll('li[data-product-id]').forEach((li) => {
      cards.set(Number(li.dataset.productId), li);
    });

    // The current product can come back in its own shelf (e.g. a Prospex page's
    // collection_line=Prospex fetch includes that Prospex). Drop it at the source so
    // narrowShelf's relax decision is never thrown off by a product that can never render.
    const ids = [...cards.keys()].filter((id) => String(id) !== String(plan.currentId));

    return { tier, productIds: ids, facets, cards, relaxed: false };
  }

  render(shelf, plan) {
    const section = document.createElement('section');
    section.className = 'related-tiers__shelf';
    section.dataset.tier = shelf.tier.id;

    const header = document.createElement('div');
    header.className = 'related-tiers__header';

    const kicker = document.createElement('span');
    kicker.className = 'related-tiers__kicker';
    kicker.textContent = shelf.tier.kicker;

    const title = document.createElement('h2');
    title.className = 'related-tiers__title h2';
    title.textContent = shelf.tier.title;

    header.append(kicker, title);

    // "See all" drills into the collection with this row's filter preset. Skipped when
    // the plan carries no label, so a family that has not defined one degrades quietly
    // rather than rendering an empty link.
    if (shelf.tier.linkLabel) {
      const link = document.createElement('a');
      link.className = 'related-tiers__link';
      link.href = buildCollectionUrl(plan, shelf.tier);
      link.textContent = shelf.tier.linkLabel;

      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = ' →';
      link.append(arrow);

      header.append(link);
    }

    const grid = document.createElement('ul');
    grid.className = 'related-tier__grid grid product-grid grid--4-col-desktop grid--2-col-tablet-down';
    grid.setAttribute('role', 'list');
    shelf.productIds.forEach((id) => grid.append(shelf.cards.get(id)));

    section.append(header, grid);
    this.append(section);
  }
}

customElements.define('related-tiers', RelatedTiers);
